<script setup lang="ts">
/**
 * UI-kit component
 * Category: 'Menus'
 * Name: 'SidebarMenu'
 * FIGMA: ''
 */
import type { SidebarMenuProps } from '@UI/Menus/SidebarMenu/types/interfaces'

import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons'

import { useSidebarMenu } from '@UI/Menus/SidebarMenu/composables/use-sidebar-menu.hook'
import { AppIcon } from '@UI/Icons/AppIcon'
import { AppLogo } from '@UI/Other/AppLogo'

import MenuNavItem from './MenuNavItem.vue'

defineOptions({
  name: 'SidebarMenu',
})

const props = withDefaults(defineProps<SidebarMenuProps>(), {
  isProduction: false,
  isDrawerOpen: false,
})

const emit = defineEmits<{
  'click:close': []
}>()

const {
  refIsOpen,
  refDirection,
  homePath,
  setMenu,
  handleClose,
} = useSidebarMenu({
  props,
  onClose: () => emit('click:close'),
})
</script>

<template>
  <div
    class="adm-menu__wrapper"
    :class="{ 'is-open': refIsOpen, 'is-drawer-open': isDrawerOpen }"
    @click.self="handleClose"
  >
    <div
      class="adm-menu"
      :class="{ 'is-open': refIsOpen }"
      @mouseenter="setMenu(true)"
      @mouseleave="setMenu(false)"
    >
      <div class="adm-menu__head">
        <NuxtLink
          :to="homePath"
          class="adm-menu__logo"
          :class="{ 'is-hidden': !refIsOpen }"
        >
          <AppLogo
            type="mark"
            size="sm"
          />
        </NuxtLink>
        <span :class="['adm-menu__label', { production: isProduction }]">
          {{ isProduction ? 'Prod' : 'Test' }}
        </span>
        <button
          type="button"
          class="adm-menu__toggle"
          @click="handleClose"
        >
          <AppIcon :icon="refDirection === 'left' ? faChevronLeft : faChevronRight" />
        </button>
      </div>
      <div class="adm-menu__body">
        <div class="adm-menu__list">
          <template
            v-for="item in items"
            :key="item.id"
          >
            <MenuNavItem
              v-if="item.visible"
              :text="item.text"
              :to="item.to"
              :icon="item.icon"
              :simple="!refIsOpen"
              :active="item.active"
              :is-external="item.isExternal"
              :never-migrate="item.neverMigrate"
              :is-production="isProduction"
              :children="item.children"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped src="../styles/_sidebar-menu.scss" />
